import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Helper to normalize various possible backend responses to a URL
function resolveUploadedUrl(resp) {
  if (!resp) return '';
  // Common shapes:
  // { url: '...' }
  if (typeof resp.url === 'string') return resp.url;
  // { file: { url: '...' } }
  if (resp.file && typeof resp.file.url === 'string') return resp.file.url;
  // { data: { url: '...' }}
  if (resp.data && typeof resp.data.url === 'string') return resp.data.url;
  // { path: '...' }
  if (typeof resp.path === 'string') return resp.path;
  return '';
}

export default function ImageUploader({ websiteId, onUploaded, onError, label, multiple = false, accept = 'image/*', className = '', type = 'thumb,original' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const apiUrl = useMemo(() => (process.env.REACT_APP_API_URL || ''), []);

  const filterFilesByAccept = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!accept || accept === '*/*') return files;
    // Minimal filter: support image/* and specific mime types like image/png
    return files.filter((f) => {
      if (accept.includes('image/*')) return f.type.startsWith('image/');
      // If accept contains comma-separated types, check any exact match
      const parts = accept.split(',').map(s => s.trim());
      return parts.some(p => p === f.type);
    });
  }, [accept]);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    if (!websiteId) {
      toast.error('Website ID is required for image upload.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You must be authenticated to upload images.');
      return;
    }

    setUploading(true);

    const uploadOne = async (file) => {
      const formData = new FormData();
      formData.append('image', file);

      const endpoint = `${apiUrl}public/images/${websiteId}/upload?type=${type}`;

      try {
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: {
            // Don't set Content-Type for FormData; browser will set correct boundary
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: formData,
        });

        if (!resp.ok) {
          // Try to parse error message
          let message = `Upload failed with status ${resp.status}`;
          try {
            const err = await resp.json();
            message = err?.message || err?.error || message;
          } catch (_) {}
          throw new Error(message);
        }

        let data = null;
        const contentType = resp.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          data = await resp.json();
        } else {
          // Some backends might return plain text URL
          const text = await resp.text();
          data = { url: text };
        }

        const url = resolveUploadedUrl(data);
        if (!url) {
          throw new Error('Upload succeeded but no URL was returned by the server.');
        }

        onUploaded?.({ success: 1, url, raw: data });
        return { success: true, url, raw: data };
      } catch (e) {
        const message = e?.message || 'Image upload failed';
        toast.error(message);
        onError?.(message);
        return { success: false, error: message };
      }
    };

    if (multiple) {
      const results = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await uploadOne(file));
      }
      setUploading(false);
      return results;
    } else {
      const result = await uploadOne(files[0]);
      setUploading(false);
      return result;
    }
  }, [apiUrl, websiteId, multiple, onUploaded, onError, type]);

  const onInputChange = useCallback((e) => {
    const files = e.target.files;
    handleFiles(files);
    // reset input so user can select the same file again if needed
    e.target.value = '';
  }, [handleFiles]);

  const openFileDialog = useCallback(() => {
    if (uploading) return;
    inputRef.current?.click();
  }, [uploading]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  }, [uploading]);

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  }, [uploading]);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    const files = filterFilesByAccept(dt?.files || []);
    if (!files || files.length === 0) return;
    handleFiles(files);
  }, [filterFilesByAccept, handleFiles]);

  const zoneStyles = {
    border: '2px dashed ' + (isDragging ? '#2563eb' : '#cbd5e1'),
    borderRadius: 8,
    padding: 16,
    textAlign: 'center',
    cursor: uploading ? 'not-allowed' : 'pointer',
    color: '#475569',
    background: isDragging ? '#eff6ff' : 'transparent',
    transition: 'all 120ms ease-in-out',
  };

  const hint = uploading
    ? 'Uploading…'
    : (label || (multiple ? 'Click or drag & drop images here' : 'Click or drag & drop an image here'));

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onInputChange}
        disabled={uploading}
        style={{ display: 'none' }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-disabled={uploading}
        onClick={openFileDialog}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFileDialog(); } }}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={zoneStyles}
      >
        <div style={{ fontSize: 14 }}>
          {hint}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
          {accept ? `Accepted: ${accept}` : 'Any file'}{multiple ? ' • Multiple allowed' : ''}
        </div>
      </div>
    </div>
  );
}

ImageUploader.propTypes = {
  websiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onUploaded: PropTypes.func,
  onError: PropTypes.func,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
};
